// @ts-check
// =============================================================================
// js/net/cdn.js — Cloudflare (smutty.stream) art routing + origin fallback.
//
// WHAT THIS IS (see docs/CDN_OFFLOAD.md)
//   Card art lives on the public GCS bucket
//   (https://storage.googleapis.com/deckbiter/…). The smutty.stream edge is a
//   pull-through cache in front of it. This module is the ONE place that decides
//   whether to try the CDN first, and it ALWAYS falls back to the bucket on any
//   failure, remembering the miss so the rest of the session skips the CDN for
//   that path (no repeated wasted round-trips).
//
//   The CDN base arrives from the server at login (userData.cdnBase ->
//   store.cdnBase). Empty string => offload DISABLED: every helper here is a
//   transparent pass-through to the origin, so the game behaves exactly as
//   before. That is the rollout kill-switch.
//
// WHO USES IT
//   - card-tile.js injects fetchArt() into the generator (the hot path: every
//     card render fetches its raw art through here).
//   - rewards.js / art-picker.js use artImgAttrs() for their <img> grids.
// =============================================================================

import { store } from '../core/store.js';

/** The GCS bucket URL prefix all card-art origin URLs start with. Exported
 *  for the rare direct-art consumer (the bunny-victory overlay loads a RAW
 *  artwork file — image-cache SRC only hands out the pre-rendered full-card
 *  "Card_<art>.png" form). */
export const ORIGIN_PREFIX = 'https://storage.googleapis.com/deckbiter/';

/** Pathnames (bucket-relative, no query) that missed the CDN this session. */
const failedPaths = new Set();

/** Current CDN base, or '' when the offload is disabled. */
function cdnBase() {
	return (store.cdnBase || '').replace(/\/+$/, '');
}

/**
 * The bucket-relative path for an origin URL (e.g. "Card_1_2.png"), or null if
 * the URL isn't a bucket art URL we should route.
 * @param {string} originUrl
 * @returns {string|null}
 */
function bucketPath(originUrl) {
	if (typeof originUrl !== 'string') return null;
	if (!originUrl.startsWith(ORIGIN_PREFIX)) return null;
	return originUrl.slice(ORIGIN_PREFIX.length).split('?')[0];
}

/**
 * Map a bucket origin URL to its CDN URL (with the ?v= cache-buster), or null
 * when the offload is off, the URL isn't routable, or this path already missed.
 * @param {string} originUrl
 * @returns {string|null}
 */
export function cdnUrlFor(originUrl) {
	const base = cdnBase();
	if (!base) return null;
	const path = bucketPath(originUrl);
	if (!path || failedPaths.has(path)) return null;
	const version = store.version ? `?v=${encodeURIComponent(store.version)}` : '';
	return `${base}/${path}${version}`;
}

/** Remember that a path missed the CDN so we don't retry it this session. */
function markFailed(originUrl) {
	const path = bucketPath(originUrl);
	if (path) failedPaths.add(path);
}

/**
 * fetch() an art asset CDN-first, falling back to the bucket origin on ANY
 * failure (non-2xx, CORS, network). The returned Response is the origin one on
 * fallback, so callers that check `response.ok` behave unchanged.
 *
 * Signature matches window.fetch so it can be injected wherever fetch is used.
 * @param {string} originUrl   The bucket origin URL.
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export async function fetchArt(originUrl, init) {
	const cdn = cdnUrlFor(originUrl);
	if (cdn) {
		try {
			const res = await fetch(cdn, init);
			if (res.ok) return res;
		} catch {
			/* network / CORS error — fall through to origin */
		}
		markFailed(originUrl);
	}
	return fetch(originUrl, init);
}

/**
 * Attributes for an <img> that loads CDN-first and falls back to the bucket on
 * error. Returns a string to splice into an innerHTML template. When the
 * offload is off it simply points at the origin.
 *
 * The onerror handler swaps to the origin once (self-clearing) so a CDN miss on
 * an <img> that can't use fetch() still recovers.
 * @param {string} originUrl
 * @returns {string}  e.g. `src="…" onerror="…"`
 */
export function artImgAttrs(originUrl) {
	const cdn = cdnUrlFor(originUrl);
	const safeOrigin = String(originUrl).replace(/"/g, '&quot;').replace(/'/g, '\\u0027');
	if (!cdn) return `src="${safeOrigin}"`;
	const safeCdn = cdn.replace(/"/g, '&quot;');
	// On failure: clear the handler, remember the miss on the client, load origin.
	return `src="${safeCdn}" onerror="this.onerror=null;this.src='${safeOrigin}'"`;
}
